import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationDialog } from '../confirmation-dialog/ConfirmationDialog';
import { Folder } from '../../types';
import { AddTaskModal } from './AddTaskModal';
import { ExistingTaskPicker } from './ExistingTaskPicker';
import { ScheduleView } from './ScheduleView';
import { ScheduleTimelineControls } from './ScheduleTimelineControls';
import { TimelineView } from './TimelineView';
import { PlannerTaskInfo } from './types';
import { useDraftGestures } from './useDraftGestures';
import { usePlannerData } from './usePlannerData';
import { usePlannerNavigation } from './usePlannerNavigation';
import { usePlannerRefs } from './usePlannerRefs';
import { usePlannerState } from './usePlannerState';
import { useTaskGestures } from './useTaskGestures';
import { useTaskModal } from './useTaskModal';
import { useTimelineCreation } from './useTimelineCreation';
import { useTimelineLayout } from './useTimelineLayout';
import { useTimelinePointerEvents } from './useTimelinePointerEvents';
import { useTimelineTaskQueries } from './useTimelineTaskQueries';
import { useTimelineWindow } from './useTimelineWindow';
import { getTaskDateUpdates } from './taskReschedule';
import './DailyPlanner.css';

interface DailyPlannerProps {
    folders: Folder[];
    setFolders: (folders: Folder[]) => void;
    onOpenTaskInCollection: (taskInfo: PlannerTaskInfo) => void;
}

const getPlannerStartDate = (date: Date | null, time: string | null, fallback: Date) => {
    const start = new Date(date || fallback);
    const [hours, minutes] = (time || '09:00').split(':').map(Number);
    start.setHours(hours, minutes, 0, 0);
    return start;
};

const getPlannerEndDate = (
    date: Date | null,
    time: string | null,
    duration: number,
    fallback: Date,
) => {
    const start = getPlannerStartDate(date, time, fallback);
    return new Date(start.getTime() + duration * 60_000);
};

export const DailyPlanner: React.FC<DailyPlannerProps> = ({
    folders, setFolders, onOpenTaskInCollection,
}) => {
    const { t } = useTranslation();
    const state = usePlannerState();
    const [pendingDelete, setPendingDelete] = useState<{ taskId: string; collectionId: string } | null>(null);
    const [taskListReturn, setTaskListReturn] = useState<{ scope: 'day' | 'month' | 'year'; date: Date } | null>(null);
    const [taskListToOpen, setTaskListToOpen] = useState<{ scope: 'day' | 'month' | 'year'; date: Date } | null>(null);
    const refs = usePlannerRefs(state.draftInteraction);
    const data = usePlannerData(folders, setFolders, state.showCompletedTasks);
    const modal = useTaskModal(state, refs, data, folders);
    const creation = useTimelineCreation(state, refs, modal.openCreateTaskModal);
    const drafts = useDraftGestures(state, refs);
    const tasks = useTaskGestures(state, refs, data);
    const queries = useTimelineTaskQueries(data.tasksWithDueDates, state, refs);
    const timelineWindow = useTimelineWindow(state, refs);
    const navigation = usePlannerNavigation(state, refs, data.tasksWithDueDates);
    const requestDeleteTask = (taskId: string, collectionId: string) => setPendingDelete({ taskId, collectionId });
    useEffect(() => {
        if (taskListReturn && !state.showAddTask && !pendingDelete) {
            setTaskListToOpen(taskListReturn);
            setTaskListReturn(null);
        }
    }, [pendingDelete, state.showAddTask, taskListReturn]);
    useTimelineLayout(state, refs, timelineWindow.updateActiveTimelineLabel);
    useTimelinePointerEvents(state, refs, {
        cancelPendingTouchCreate: creation.cancelPendingTouchCreate,
        expandTimelineWindowAtEdge: timelineWindow.expandTimelineWindowAtEdge,
        getDraftSelectionForPointer: drafts.getDraftSelectionForPointer,
        handleCreationPointerMove: creation.handleCreationPointerMove,
        handleCreationPointerUp: creation.handleCreationPointerUp,
        handleDraftPointerMove: drafts.handleDraftPointerMove,
        handleDraftPointerUp: drafts.handleDraftPointerUp,
        handleResizePointerMove: tasks.handleResizePointerMove,
        handleResizePointerUp: tasks.handleResizePointerUp,
        handleTaskPointerMove: tasks.handleTaskPointerMove,
        handleTaskPointerUp: tasks.handleTaskPointerUp,
        updateActiveTimelineLabel: timelineWindow.updateActiveTimelineLabel,
    });
    return (
        <div className="daily-planner">
            <div className="planner-timeline timeline-mode"
                ref={refs.timelineRef} onScroll={timelineWindow.handleTimelineScroll}>
                {state.showSchedule ? (
                    <>
                    <ScheduleTimelineControls showCompletedTasks={state.showCompletedTasks}
                        onShowCompletedTasksChange={state.setShowCompletedTasks} onScaleChange={(scale) => {
                        state.setShowSchedule(false);
                        navigation.handleTimelineScaleChange(scale);
                    }} />
                    <ScheduleView tasks={data.tasksWithDueDates}
                        habitsForToday={data.habitsForToday} getFolderName={data.getFolderName}
                        getCollectionName={data.getCollectionName} onOpenTask={navigation.handleOpenScheduledTask}
                        onEditTask={modal.handleEditTask} onToggleComplete={data.handleToggleComplete}
                        onToggleSubtask={data.handleToggleComplete}
                        onOpenTaskInCollection={onOpenTaskInCollection}
                        onDeleteTask={requestDeleteTask} onCreateTask={modal.handleAddTaskForScheduleDate}
                        onPickExistingTask={modal.handlePickExistingTaskForScheduleDate}
                        onAdvanceHabit={data.handleAdvanceHabit} onRescheduleTask={data.handleRescheduleTask} />
                    </>
                ) : (
                    <TimelineView activeTimelineLabel={state.activeTimelineLabel}
                        activeTimelineDateKey={state.activeTimelineDateKey}
                        addTaskDate={state.addTaskDate} addTaskDuration={state.addTaskDuration}
                        addTaskTime={state.addTaskTime} currentMouseY={state.currentMouseY}
                        dragStartHour={state.dragStartHour} dragStartOffset={state.dragStartOffset}
                        dragTaskStartTopY={state.dragTaskStartTopY}
                        dragTaskStartY={state.dragTaskStartY}
                        draggingTask={state.draggingTask} draftSelection={state.draftSelection}
                        getCollectionName={data.getCollectionName} getFolderName={data.getFolderName}
                        getTasksForDay={queries.getTasksForDay} getTasksForMonth={queries.getTasksForMonth}
                        getTasksForYear={queries.getTasksForYear}
                        getTimelineDateFromClientY={tasks.getTimelineDateFromClientY}
                        isDragging={state.isDragging} onCancelDraftSelection={creation.cancelDraftSelection}
                        onCreateTimedTask={modal.handleCreateTimelineTask}
                        onCreateFlexibleTask={modal.handleCreateFlexibleTask}
                        onGoToNow={navigation.handleGoToNow}
                        onGoToNextTask={navigation.handleGoToNextTask}
                        onOpenTask={navigation.handleOpenScheduledTask}
                        onOpenSchedule={() => state.setShowSchedule(true)}
                        onShowCompletedTasksChange={state.setShowCompletedTasks}
                        onOpenDayInDaysView={navigation.handleOpenDayInDaysView}
                        onMoveTaskToDate={(taskInfo, date) => data.updateTask(
                            taskInfo.collectionId,
                            taskInfo.task.id,
                            getTaskDateUpdates(taskInfo.task, date),
                        )}
                        onPickExistingTask={modal.handlePickExistingTaskForScheduleDate}
                        onRequestTaskListReturn={(scope, date) => setTaskListReturn({ scope, date: new Date(date) })}
                        onTaskListOpened={() => setTaskListToOpen(null)}
                        onZoomOutToParent={navigation.handleZoomOutToParent}
                        onConfirmDraftSelection={creation.confirmDraftSelection}
                        onDraftPointerDown={drafts.handleDraftPointerDown}
                        onDraftResizePointerDown={drafts.handleDraftResizePointerDown}
                        onDeleteTask={requestDeleteTask} onEditTask={modal.handleEditTask}
                        onHourPointerDown={creation.handleTimelineHourPointerDown}
                        onResizeLongPress={tasks.handleResizeLongPress}
                        onScaleChange={navigation.handleTimelineScaleChange}
                        onSelectedDateChange={state.setSelectedDate}
                        onTaskLongPress={tasks.handleTaskLongPress}
                        onToggleComplete={data.handleToggleComplete} resizingTask={state.resizingTask}
                        resizeStartY={state.resizeStartY} selectedDate={state.selectedDate}
                        showCompletedTasks={state.showCompletedTasks}
                        scrollContainerRef={refs.timelineRef}
                        taskListToOpen={taskListToOpen}
                        tasksWithDueDates={data.tasksWithDueDates} timelineScale={state.timelineScale}
                        timelineWindow={state.timelineWindow} timeSlotHeight={state.timeSlotHeight} />
                )}
            </div>
            {state.showAddTask && (
                <AddTaskModal editingTask={state.editingTask}
                    startDateTime={getPlannerStartDate(state.addTaskDate, state.addTaskTime, state.selectedDate)}
                    endDateTime={getPlannerEndDate(
                        state.addTaskDate,
                        state.addTaskTime,
                        state.addTaskDuration,
                        state.selectedDate,
                    )}
                    isFlexibleTask={state.isFlexibleTask} flexiblePrecision={state.flexiblePrecision}
                    flexibleEndDate={state.flexibleEndDate}
                    newTaskTitle={state.newTaskTitle}
                    onTitleChange={state.setNewTaskTitle} onSubmit={modal.handleAddTask}
                    onCancel={modal.handleCancelAddTask} />
            )}
            {state.existingTaskPickerDate && (
                <ExistingTaskPicker tasks={data.unscheduledTasks} getFolderName={data.getFolderName}
                    getCollectionName={data.getCollectionName}
                    onSelect={modal.handleSelectExistingTaskForSchedule}
                    onClose={() => state.setExistingTaskPickerDate(null)} />
            )}
            {pendingDelete && <ConfirmationDialog title={t('taskElement.deleteTask')}
                message={t('taskElement.deleteTaskConfirm')} isDanger
                onCancel={() => setPendingDelete(null)} onConfirm={() => {
                    data.handleDeleteTask(pendingDelete.taskId, pendingDelete.collectionId);
                    setPendingDelete(null);
                }} />}
        </div>
    );
};
